import { useUser } from "@clerk/clerk-react";
import { AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { BillingSection } from "@/components/account/BillingSection";
import { CertificatesSection } from "@/components/account/CertificatesSection";
import { NameRow } from "@/components/account/NameRow";
import { PortfolioSection } from "@/components/account/PortfolioSection";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { computeProgrammeStatus, formatStatus } from "@/utils/status";

const PROGRAMME_PILLS: Record<string, string> = {
  in_progress: "bg-[#DCF2E4] text-[#1E6F4F]",
  not_started: "bg-[#FBF6E7] text-[#8A6D1F]",
  completed: "bg-[#EAE6D8] text-[#6B7267]",
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-4 border-t border-content-border/60 px-7 py-5 sm:grid-cols-[170px_1fr]">
      <div className="text-[13px] font-bold text-[#5C6E63]">{label}</div>
      <div className="text-[15px] font-semibold text-card-foreground">{value}</div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[11px] bg-[#F7F4EA] px-[18px] py-[15px]">
      <div className="text-[10.5px] font-bold tracking-[.14em] text-content-muted">{label}</div>
      <div className="mt-1.5 text-[15.5px] font-bold text-card-foreground">{value}</div>
    </div>
  );
}

export default function AccountPage() {
  const { user } = useUser();
  const { enrollment, isLoading } = useEnrollment();

  const displayName = user?.fullName || "User";
  const email = user?.primaryEmailAddress?.emailAddress ?? "—";
  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join("") ||
    email[0]?.toUpperCase() ||
    "U";

  const programmeStatus = enrollment
    ? computeProgrammeStatus(enrollment.programme.start_date, enrollment.programme.end_date)
    : null;
  const progressPct = enrollment
    ? Math.min(
        100,
        Math.max(0, Math.round((enrollment.current_week / enrollment.programme.total_weeks) * 100))
      )
    : 0;
  const weeksRemaining = enrollment
    ? Math.max(0, enrollment.programme.total_weeks - enrollment.current_week)
    : 0;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto flex flex-col gap-[22px]">
        {/* Header */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
            Preference
          </p>
          <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-[-.015em] text-content-foreground">
            Account
          </h1>
          <p className="mt-1.5 text-[15px] text-[#5C6E63]">
            Manage your profile, programme and billing settings.
          </p>
        </div>

        {/* Profile card */}
        <div className="overflow-hidden rounded-[14px] border border-content-border bg-card">
          <div className="flex flex-wrap items-center gap-[18px] bg-[#013220] px-7 py-[26px]">
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={displayName}
                className="h-16 w-16 flex-none rounded-full object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 flex-none items-center justify-center rounded-full bg-gradient-to-br from-brand to-[#1E6F4F] text-xl font-extrabold text-white">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[21px] font-extrabold text-white">{displayName}</div>
              <div className="mt-0.5 truncate text-[13.5px] text-[#F2EFE3]/65">{email}</div>
            </div>
          </div>
          <NameRow />
          <InfoRow label="Email" value={email} />
          <InfoRow
            label="Member since"
            value={user?.createdAt ? formatDate(user.createdAt.toISOString()) : "—"}
          />
        </div>

        {/* Active programme */}
        <div className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
          <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">
            ACTIVE PROGRAMME
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-content-muted" />
            </div>
          ) : enrollment ? (
            <>
              <div className="mt-1.5 flex flex-wrap items-start gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-[26px] font-extrabold tracking-[-.01em] text-card-foreground">
                      {enrollment.programme.name}
                    </h2>
                    {programmeStatus && (
                      <span
                        className={`rounded-full px-[11px] py-1.5 text-[10.5px] font-bold tracking-[.1em] uppercase ${PROGRAMME_PILLS[programmeStatus] ?? PROGRAMME_PILLS.completed}`}
                      >
                        {formatStatus(programmeStatus)}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to="/"
                  className="flex items-center gap-2 rounded-[10px] bg-[#013220] px-5 py-3 text-[13px] font-bold text-[#F2EFE3] transition-colors hover:bg-[#0A4530]"
                >
                  Go to Dashboard
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                </Link>
              </div>
              <div className="mt-[22px] flex items-baseline justify-between">
                <span className="text-sm font-bold text-card-foreground">
                  Week {enrollment.current_week} of {enrollment.programme.total_weeks}
                </span>
                <span className="font-serif text-[13px] italic text-content-muted">
                  {weeksRemaining > 0
                    ? `${weeksRemaining} week${weeksRemaining === 1 ? "" : "s"} remaining`
                    : "Final week"}
                </span>
              </div>
              <div className="mt-[9px] h-2 overflow-hidden rounded-full bg-[#EFECDE]">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#013220] to-brand transition-[width] duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3.5 lg:grid-cols-4">
                <StatTile label="START DATE" value={formatDate(enrollment.programme.start_date)} />
                <StatTile label="END DATE" value={formatDate(enrollment.programme.end_date)} />
                <StatTile
                  label="ASSIGNMENTS"
                  value={`${enrollment.programme.total_weeks * 2} total`}
                />
                <StatTile label="ENROLLED" value={formatDate(enrollment.enrolled_at)} />
              </div>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-content-border">
                <AlertCircle className="h-6 w-6 text-content-muted" />
              </div>
              <p className="font-semibold text-content-foreground">No active programme</p>
              <p className="mt-1 text-sm text-content-muted">
                You are not currently enrolled in any programme
              </p>
            </div>
          )}
        </div>

        {/* Portfolio */}
        <PortfolioSection programmeId={enrollment?.programme_id ?? null} />

        {/* Certificates */}
        <CertificatesSection />

        {/* Payment & Billing */}
        <BillingSection />
      </div>
    </DashboardLayout>
  );
}
